import { useState } from 'react';
import { Play, Pause, Music, Download, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatTime } from '@/utils/format';

interface SongHeroProps {
  song: {
    id: string;
    title: string;
    artist: string | null;
    album: string | null;
    spotify_preview_url: string | null;
    spotify_duration_ms: number | null;
    spotify_explicit: boolean;
    album_cover_url: string | null;
    sequence_count: number;
    total_downloads: number;
    average_rating: number;
  };
  sequenceCount: number;
}

export default function SongHero({ song, sequenceCount }: SongHeroProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [audio] = useState(() => 
    song.spotify_preview_url ? new Audio(song.spotify_preview_url) : null
  );

  const handlePlayPause = () => {
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  // Clean up audio on unmount
  useState(() => {
    return () => {
      if (audio) {
        audio.pause();
        audio.src = '';
      }
    };
  });

  return (
    <div className="relative bg-linear-to-b from-primary/10 to-background">
      <div className="container mx-auto px-4 py-12">
        <div className="flex flex-col md:flex-row gap-8 items-start">
          {/* Album Art */}
          <div className="relative group">
            <div className="w-64 h-64 md:w-80 md:h-80 rounded-lg overflow-hidden shadow-xl">
              {song.album_cover_url ? (
                <img
                  src={song.album_cover_url}
                  alt={`${song.album || song.title} cover`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-muted flex items-center justify-center">
                  <Music className="w-24 h-24 text-muted-foreground" />
                </div>
              )}
            </div>
            
            {/* Play button overlay */}
            {song.spotify_preview_url && (
              <Button
                size="lg"
                className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-full h-16 w-16 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={handlePlayPause}
              >
                {isPlaying ? (
                  <Pause className="h-6 w-6" />
                ) : (
                  <Play className="h-6 w-6 ml-1" />
                )}
              </Button>
            )}
          </div>

          {/* Song Info */}
          <div className="flex-1 space-y-4">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-2">
                {song.title}
              </h1>
              {song.artist && (
                <p className="text-xl text-muted-foreground">
                  by {song.artist}
                </p>
              )}
              {song.album && (
                <p className="text-lg text-muted-foreground">
                  {song.album}
                </p>
              )}
            </div>

            {/* Stats */}
            <div className="flex flex-wrap gap-4">
              <Badge variant="secondary" className="px-4 py-2">
                <Music className="mr-2 h-4 w-4" />
                {sequenceCount} Sequences
              </Badge>
              
              <Badge variant="secondary" className="px-4 py-2">
                <Download className="mr-2 h-4 w-4" />
                {song.total_downloads.toLocaleString()} Downloads
              </Badge>

              {song.average_rating > 0 && (
                <Badge variant="secondary" className="px-4 py-2">
                  <Star className="mr-2 h-4 w-4 fill-current" />
                  {song.average_rating.toFixed(1)} Rating
                </Badge>
              )}

              {song.spotify_explicit && (
                <Badge variant="destructive">
                  Explicit
                </Badge>
              )}
            </div>

            {/* Duration */}
            {song.spotify_duration_ms && (
              <p className="text-muted-foreground">
                Duration: {formatTime(Math.floor(song.spotify_duration_ms / 1000))}
              </p>
            )}

            {/* Preview Player */}
            {song.spotify_preview_url && (
              <div className="pt-4">
                <Button
                  size="lg"
                  onClick={handlePlayPause}
                  className="min-w-[200px]"
                >
                  {isPlaying ? (
                    <>
                      <Pause className="mr-2 h-5 w-5" />
                      Pause Preview
                    </>
                  ) : (
                    <>
                      <Play className="mr-2 h-5 w-5" />
                      Play 30s Preview
                    </>
                  )}
                </Button>
                <p className="text-sm text-muted-foreground mt-2">
                  Preview provided by Spotify
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}